import { Lock, Paperclip, Send } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import type { Message } from '@/types/request';

export function MessageBubble({ message }: { message: Message }) {
  if (message.authorKind === 'SYSTEM') {
    return (
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1.5 text-caption text-ink-muted bg-canvas-hover rounded-pill px-3 py-1">
          {message.body}
        </div>
      </div>
    );
  }

  const isStaff = message.authorKind === 'STAFF';
  return (
    <div className={cn('flex gap-2', isStaff ? 'justify-end' : 'justify-start')}>
      {!isStaff && <Avatar name={message.authorName} size="sm" />}
      <div className={cn('max-w-[78%]')}>
        <div
          className={cn(
            'flex items-center gap-2 mb-0.5',
            isStaff && 'justify-end',
          )}
        >
          <span className="text-caption font-semibold text-ink">
            {message.authorName}
          </span>
          <span className="text-caption text-ink-muted">
            {formatDateTime(message.createdAt)}
          </span>
        </div>
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-body leading-relaxed',
            isStaff
              ? 'bg-navy-deep text-white rounded-tr-sm'
              : 'bg-white text-ink border border-line rounded-tl-sm',
            message.internal && 'bg-status-warning-bg text-status-warning-text border-status-warning/40',
          )}
        >
          {message.internal && (
            <div className="flex items-center gap-1 text-caption opacity-80 mb-1">
              <Lock className="h-3 w-3" />
              Internal note — not sent to customer
            </div>
          )}
          {message.body}
        </div>
      </div>
      {isStaff && <Avatar name={message.authorName} size="sm" />}
    </div>
  );
}

export function ChatComposer({
  onSend,
  placeholder = 'Reply to customer…',
}: {
  onSend: (body: string, internal: boolean) => void;
  placeholder?: string;
}) {
  const [value, setValue] = useState('');
  const [internal, setInternal] = useState(false);

  function send() {
    const body = value.trim();
    if (!body) return;
    onSend(body, internal);
    setValue('');
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  }

  return (
    <div className="bg-white">
      <div className="px-4 pt-3 pb-2">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          placeholder={internal ? 'Internal note — only visible to staff' : placeholder}
          rows={2}
          className={cn(
            'w-full resize-none outline-none text-body text-ink placeholder:text-ink-hint',
            'bg-transparent',
          )}
        />
      </div>
      <div className="flex items-center gap-2 px-3 pb-3">
        <button
          type="button"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-muted hover:bg-canvas"
          aria-label="Attach"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setInternal((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-caption font-semibold',
            internal
              ? 'bg-status-warning-bg text-status-warning-text'
              : 'text-ink-muted hover:bg-canvas',
          )}
        >
          <Lock className="h-3.5 w-3.5" />
          Internal note
        </button>
        <div className="flex-1" />
        <span className="text-caption text-ink-hint hidden sm:inline">
          ⌘ + Enter to send
        </span>
        <Button size="sm" onClick={send} iconRight={<Send className="h-3.5 w-3.5" />}>
          Send
        </Button>
      </div>
    </div>
  );
}
